/**
 * main.ts — English Literature EduTool entry point.
 *
 * Boot sequence: load manifest + init data service → AppShell → StageRouter.
 *
 * The AppShell pulls the initial locale from the bridge's InitPayload. In
 * standalone (no KMP host) the manifest declares ["en", "it"] so the bridge
 * defaults to "en" — exactly what we want as the primary UI locale.
 */

// Styles — order matters: tokens first, then base, then layout, then views
import '@/styles/tokens-base.css';
import '@/styles/tokens-brand.css';
import '@/styles/base.css';
import '@/styles/layout.css';
import '@/styles/views.css';
import '@/styles/lesson-builder.css';
import '@/styles/responsive.css';

import { AppShell } from '@/core/AppShell';
import { applySurface } from '@/core/surface';
import type { ToolManifest } from '@/bridge/bridge-types';

// Sidebar sections
import { BrandHeader } from '@/components/sidebar/BrandHeader';
import { SystemTray } from '@/components/sidebar/SystemTray';
import { ViewNav } from '@/components/sidebar/ViewNav';
import { FilterPanel } from '@/components/sidebar/FilterPanel';

// Stage router
import { StageRouter } from '@/views/StageRouter';

// Data layer
import { StaticDataService } from '@/services/StaticDataService';
import type { IDataService } from '@/services/IDataService';
import { setDataService } from '@/services/data-registry';
import { lessonBuilderService } from '@/services/lesson-builder-service';

async function loadManifest(): Promise<ToolManifest> {
  const response = await fetch('/edutool.manifest.json');
  if (!response.ok) throw new Error('Failed to load edutool.manifest.json');
  return response.json() as Promise<ToolManifest>;
}

async function bootDataService(): Promise<IDataService> {
  const service = new StaticDataService();
  await service.init();
  setDataService(service);
  const n = service.listAuthors().length;
  console.info(`[letteratura-inglese] catalog ready — ${n} authors`);
  return service;
}

async function main(): Promise<void> {
  try {
    // SUPERFICIE prima di tutto: la scala LIM (html[data-surface=lim]) deve
    // esserci già quando la shell monta (sidebar chiusa, scala proiezione).
    const surface = applySurface();

    const [manifest] = await Promise.all([loadManifest(), bootDataService()]);

    const shell = new AppShell({
      manifest,
      sections: {
        brand: BrandHeader,
        nav: ViewNav,
        list: FilterPanel,
        tray: SystemTray,
      },
      stageRenderer: StageRouter,
      defaultSidebarOpen: true,
    });

    await shell.boot();

    // Force LIGHT theme regardless of the bridge default. ed-tool-letteratura-inglese
    // is a single-palette light tool (philosophy is dark, so the shared bridge
    // ships `theme: 'dark'` by default — irrelevant for us).
    shell.applyTheme('light');

    // Lesson-builder: attiva il bottone "+" se URL contiene userId e role è
    // docente. MAI su LIM: in aula si proietta, non si compone (matrice
    // contesti: docente-su-LIM = solo fruizione).
    if (surface !== 'lim') lessonBuilderService.init(manifest.id, manifest.subject);

    // Su LIM il "← Eduverse" torna al MENU Aula del hub (i 3 box), non alla
    // home: è il percorso d'uscita naturale del docente in classe.
    if (surface === 'lim') {
      const back = document.querySelector<HTMLAnchorElement>('.eduverse-back');
      const hubUrl = new URLSearchParams(window.location.search).get('hubUrl');
      if (back && hubUrl) {
        back.href = `${hubUrl.replace(/\/+$/, '')}/lim`;
        back.innerHTML = '<span aria-hidden="true">←</span> Menu';
        back.title = 'Torna al menu Aula';
      }
    }
  } catch (err) {
    console.error('[main] boot failed:', err);
  }
}

main().catch(console.error);
